// hooks/useEvents.js
import { useState, useEffect, useCallback } from "react";
import { collection, query, orderBy, onSnapshot } from "firebase/firestore";
import { db } from "../firebase";
import { 
  getEvents, 
  getFeaturedEvents, 
  createEvent, 
  updateEvent, 
  deleteEvent 
} from "../utils/api";

export const useEvents = () => {
  const [events, setEvents] = useState([]);
  const [featuredEvents, setFeaturedEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Real-time listener for events
  useEffect(() => {
    const eventsCollection = collection(db, "events");
    const q = query(eventsCollection, orderBy("date", "asc"));

    const unsubscribe = onSnapshot(
      q,
      (snapshot) => {
        const fetchedEvents = snapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
          date: doc.data().date?.toDate ? doc.data().date.toDate() : doc.data().date,
        }));
        setEvents(fetchedEvents);
        setFeaturedEvents(fetchedEvents.filter((event) => event.featured));
        setLoading(false);
      },
      (err) => {
        console.error("Error listening to events:", err);
        setError(err.message);
        setLoading(false);
      }
    );

    return () => unsubscribe();
  }, []);

  // Fetch all events (manual refresh)
  const fetchEvents = useCallback(async () => {
    try {
      setLoading(true);
      const data = await getEvents();
      setEvents(data);
      setFeaturedEvents(data.filter((event) => event.featured));
      setError(null);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  // Fetch featured events only
  const fetchFeaturedEvents = useCallback(async (limit = 3) => {
    try {
      setLoading(true);
      const data = await getFeaturedEvents(limit);
      setFeaturedEvents(data);
      setError(null);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  // Create new event
  const addEvent = useCallback(async (eventData) => {
    try {
      setLoading(true);
      const newEvent = await createEvent(eventData);
      setError(null);
      return newEvent;
    } catch (err) {
      setError(err.message);
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  // Update existing event
  const editEvent = useCallback(async (eventId, eventData) => {
    try {
      setLoading(true);
      const updatedEvent = await updateEvent(eventId, eventData);
      setError(null);
      return updatedEvent;
    } catch (err) {
      setError(err.message);
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  // Delete event
  const removeEvent = useCallback(async (eventId) => {
    try {
      setLoading(true);
      await deleteEvent(eventId);
      setError(null);
      return true;
    } catch (err) {
      setError(err.message);
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  // Filter events by category
  const getEventsByCategory = useCallback(
    (category) => {
      if (category === "all" || !category) {
        return events;
      }
      return events.filter(
        (event) => event.category?.toLowerCase() === category.toLowerCase()
      );
    },
    [events]
  );

  // Get upcoming events (from today onwards)
  const getUpcomingEvents = useCallback(
    (limit) => {
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      
      const upcoming = events.filter((event) => {
        const eventDate = event.date instanceof Date ? event.date : new Date(event.date);
        return eventDate >= today;
      });

      return limit ? upcoming.slice(0, limit) : upcoming;
    },
    [events]
  );

  return {
    events,
    featuredEvents,
    loading,
    error,
    fetchEvents,
    fetchFeaturedEvents,
    addEvent,
    editEvent,
    removeEvent,
    getEventsByCategory,
    getUpcomingEvents,
  };
};

export default useEvents;
